import Sound from './Sound';
import util from './util';
import YoutubePlayer from './player/YoutubePlayer';
import SoundcloudPlayer from './player/SoundcloudPlayer';
import SpotifyPlayer from './player/SpotifyPlayer';

const drivers = [YoutubePlayer, SoundcloudPlayer, SpotifyPlayer];

function detectDriver(url) {
	return drivers.find(D => D.urlMatches(url)) ?? null;
}

export default class MediaSound extends Sound {
	#elemId;
	#song;
	#driver = null;
	#driverAc = null;
	#stopAc = null;

	constructor(elemId, song) {
		super();
		this.#elemId = elemId;
		this.#song = song;
	}

	async load() {
		this._setReadyState('ready');
	}

	play(signal) {
		const song = this.#song;

		this.#stopAc = new AbortController();
		const combined = signal
			? util.signalAny([this.#stopAc.signal, signal])
			: this.#stopAc.signal;

		return new Promise((resolve, reject) => {
			if(combined.aborted) return resolve();

			let timeLimitId = null;

			const cleanup = async () => {
				if(timeLimitId) clearTimeout(timeLimitId);
				this.#stopAc = null;
				await this._destroyDriver();
			};

			combined.addEventListener('abort', async () => {
				await cleanup();
				resolve();
			}, { once: true });

			if(song.timeLimit) {
				timeLimitId = setTimeout(() => this.#stopAc?.abort(), song.timeLimit * 1000);
			}

			this._loadDriver(song.url)
				.then(async () => {
					if(combined.aborted) {
						await this._destroyDriver();
						return;
					}

					const driverAc = this.#driverAc = new AbortController();

					this.#driver.addEventListener('status', ev => {
						switch(ev.detail.status) {
							case 'loading': this._setReadyState('loading'); break;
							case 'playing': this._setReadyState('playing'); break;
							case 'finished': this.#stopAc?.abort(); break;
						}
					}, { signal: driverAc.signal });

					this.#driver.addEventListener('error', ev => {
						this._setReadyState('error', ev.detail);
						setTimeout(() => {
							cleanup().then(() => reject(new TypeError(`Could not play ${song.url}: ${ev.detail}`)));
						}, 3000);
					}, { signal: driverAc.signal });

					await this.#driver.load(song.url, song.info?.start_time, song.timeLimit);
				})
				.catch(err => cleanup().then(() => reject(err)));
		});
	}

	pause()  { return this.#driver?.pause(); }
	resume() { return this.#driver?.play();  }

	stop() { this.#stopAc?.abort(); }

	async _loadDriver(url) {
		await this._destroyDriver();
		const Cls = detectDriver(url);
		if(!Cls) throw new TypeError(`No player available for: ${url}`);
		this.#driver = new Cls(this.#elemId);
	}

	async _destroyDriver() {
		if(!this.#driver) return;
		this.#driverAc?.abort();
		this.#driverAc = null;
		const driver = this.#driver;
		this.#driver = null;
		try { await driver.cleanup(); } catch(e) { console.warn('driver cleanup:', e); }
	}
}
